import { QUERY_KEY_ENTITY, QUERY_KEY_TYPE } from '../../constants';
import { baseApi } from './base.api';

export const transactionApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getGatewayBudget: build.query({
      query: (id) => `/v1/whitelabel/gateway-budget/${id}`,
      providesTags: (res, err, id) => [
        { type: QUERY_KEY_ENTITY.BUDGET, id: Number(id) || 0 },
      ],
    }),
    getGatewayTransactions: build.query({
      query: (wlid) => `/v1/whitelabel/gateway-budget/${wlid}/transactions`,
      providesTags: (res, err, wlid) => [
        { type: QUERY_KEY_ENTITY.WLID_TRANSACTIONS, id: Number(wlid) || 0 },
      ],
      transformResponse: ({ transactions = [] }) => transactions,
    }),
    getClientTransactions: build.query({
      query: ({ ts, ...params }) => ({
        url: `/v1/whitelabel/transactions`,
        params,
      }),
      transformResponse: ({ transactions = [] }) => transactions,
      providesTags: [
        { type: QUERY_KEY_ENTITY.CLIENT_TRANSACTIONS, id: QUERY_KEY_TYPE.LIST },
      ],
    }),
    getPublisherTransactions: build.query({
      query: ({ ts, ...params }) => ({
        url: '/v1/payment',
        params,
      }),
      providesTags: [
        {
          type: QUERY_KEY_ENTITY.PUBLISHER_PAYMENT_TRANSACTIONS,
          id: QUERY_KEY_TYPE.LIST,
        },
      ],
    }),
    getPublisherTransactionDetails: build.query({
      query: (paymentNumber) => `/v1/payment/${paymentNumber}`,
      providesTags: (res, err, arg) => [
        {
          type: QUERY_KEY_ENTITY.PUBLISHER_PAYMENT_TRANSACTIONS,
          id: arg,
        },
      ],
    }),
    createPublisherTransaction: build.mutation({
      query: (args) => ({
        url: '/v1/payment',
        method: 'POST',
        body: { transactions: args },
      }),
      invalidatesTags: (res, err) =>
        err
          ? []
          : [
              {
                type: QUERY_KEY_ENTITY.UGW_TRANSACTIONS,
                id: QUERY_KEY_TYPE.LIST,
              },
              {
                type: QUERY_KEY_ENTITY.PUBLISHER_PAYMENT_TRANSACTIONS,
                id: QUERY_KEY_TYPE.LIST,
              },
              {
                type: QUERY_KEY_ENTITY.CLIENT_TRANSACTIONS,
                id: QUERY_KEY_TYPE.LIST,
              },
            ],
    }),
    cancelPublisherTransaction: build.mutation({
      query: (args) => ({
        url: '/v1/payment/cancel',
        method: 'POST',
        body: { transactionNumbers: args },
      }),
      invalidatesTags: (res, err, args) =>
        err
          ? []
          : [
              {
                type: QUERY_KEY_ENTITY.UGW_TRANSACTIONS,
                id: QUERY_KEY_TYPE.LIST,
              },
              {
                type: QUERY_KEY_ENTITY.PUBLISHER_PAYMENT_TRANSACTIONS,
                id: QUERY_KEY_TYPE.LIST,
              },
            ],
    }),
    approvePayment: build.mutation({
      query: (arg) => ({
        url: '/v1/payment/approve',
        method: 'POST',
        body: { payments: arg },
      }),
      invalidatesTags: (res, err, arg) =>
        err
          ? []
          : [
              {
                type: QUERY_KEY_ENTITY.UGW_TRANSACTIONS,
                id: QUERY_KEY_TYPE.LIST,
              },
              {
                type: QUERY_KEY_ENTITY.PUBLISHER_PAYMENT_TRANSACTIONS,
                id: QUERY_KEY_TYPE.LIST,
              },
              {
                type: QUERY_KEY_ENTITY.CLIENT_TRANSACTIONS,
                id: QUERY_KEY_TYPE.LIST,
              },
              ...arg.map((id) => ({
                type: QUERY_KEY_ENTITY.PUBLISHER_PAYMENT_TRANSACTIONS,
                id,
              })),
            ],
    }),
    completePayment: build.mutation({
      query: (arg) => ({
        url: '/v1/payment/complete',
        method: 'POST',
        body: { payments: arg },
      }),
      invalidatesTags: (res, err, arg) =>
        err
          ? []
          : [
              {
                type: QUERY_KEY_ENTITY.UGW_TRANSACTIONS,
                id: QUERY_KEY_TYPE.LIST,
              },
              {
                type: QUERY_KEY_ENTITY.PUBLISHER_PAYMENT_TRANSACTIONS,
                id: QUERY_KEY_TYPE.LIST,
              },
              {
                type: QUERY_KEY_ENTITY.CLIENT_TRANSACTIONS,
                id: QUERY_KEY_TYPE.LIST,
              },
              ...arg.map((id) => ({
                type: QUERY_KEY_ENTITY.PUBLISHER_PAYMENT_TRANSACTIONS,
                id,
              })),
            ],
    }),
    getUGWTransactions: build.query({
      query: ({ ts, ...filters }) => ({
        url: '/v1/publisher/endpoint-transactions',
        method: 'POST',
        body: { filters },
      }),
      providesTags: [
        { type: QUERY_KEY_ENTITY.UGW_TRANSACTIONS, id: QUERY_KEY_TYPE.LIST },
      ],
    }),
    updateGatewayBudget: build.mutation({
      query: (arg) => ({
        url: `/v1/whitelabel/gateway-budget/${arg.id}`,
        method: 'POST',
        body: arg,
      }),
      invalidatesTags: (res, err, { id }) =>
        err
          ? []
          : [
              { type: QUERY_KEY_ENTITY.BUDGET, id },
              { type: QUERY_KEY_ENTITY.SETTINGS },
              { type: QUERY_KEY_ENTITY.WLID_TRANSACTIONS, id: res.wlid },
            ],
    }),
    downloadTransactions: build.mutation({
      query: (arg) => ({
        url: '/v1/publisher/endpoint-transactions/download',
        method: 'POST',
        body: arg,
        responseHandler: (res) => res.blob(),
      }),
    }),
    getPlatformSettings: build.query({
      query: (arg) => ({
        url: '/v1/whitelabel/platform-settings',
      }),
      providesTags: [{ type: QUERY_KEY_ENTITY.SETTINGS }],
    }),
  }),
});

export const {
  useGetGatewayBudgetQuery,
  useGetGatewayTransactionsQuery,
  useUpdateGatewayBudgetMutation,
  useGetClientTransactionsQuery,
  useGetPublisherTransactionsQuery,
  useGetUGWTransactionsQuery,
  useDownloadTransactionsMutation,
  useGetPublisherTransactionDetailsQuery,
  useCreatePublisherTransactionMutation,
  useCancelPublisherTransactionMutation,
  useApprovePaymentMutation,
  useCompletePaymentMutation,
  useGetPlatformSettingsQuery,
} = transactionApi;
